import React, { useState } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext.jsx";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

const css = `
:root{
  /* Pricing tokens (used elsewhere, harmless here) */
  --prc-accent:#6E5BFF;
  --prc-accent-2:#A78BFA;
  --prc-ink:#111318;
  --prc-text:#0b0b0c;
  --prc-muted:#6b7280;
  --prc-bg: radial-gradient(1200px 600px at 50% -10%, #f4f6ff, #f7f9fc 40%, #f7f9fc);
  --prc-card: rgba(255,255,255,.72);
  --prc-card-solid:#fff;
  --prc-border: rgba(17, 24, 39, .08);

  /* Home tokens */
  --ps-bg:
    radial-gradient(1200px 600px at 80% 20%, rgba(129,161,255,.22), transparent 60%),
    radial-gradient(900px 500px at 10% 80%, rgba(255,170,210,.18), transparent 60%),
    linear-gradient(180deg, #ffffff, #fbfbff 40%, #ffffff 100%);
  --ps-black:#0b0b0c;
  --ps-muted:#6b7280;
  --ps-accent:#6E5BFF;
  --ps-accent-2:#A78BFA;
  --ps-line:#eaeaef;
  --ps-card:#fff;
  --nav-h: 56px;
  --nav-border: rgba(17, 24, 39, .08);
}

/* Layout + scrolling */
.page-scroll {
  height: calc(100vh - var(--nav-h, 56px));
  overflow: auto;
  scroll-behavior: smooth;
}

@media (max-width: 640px) {
  .page-scroll {
    height: calc(100svh - var(--nav-h, 56px));
  }
}

/* Custom scrollbar on the inner scroll area */
.page-scroll {
  scrollbar-width: thin;
  scrollbar-color: #a78bfa #eef1ff;
}
.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track {
  background:#eef1ff;
  border-left:1px solid var(--nav-border, rgba(17,24,39,.08));
}
.page-scroll::-webkit-scrollbar-thumb {
  background:linear-gradient(180deg,#c7d2fe,#a78bfa);
  border:2px solid #eef1ff;
  border-radius:8px;
}

/* body overflow is handled globally in index.css */
html, body { height: 100%; overflow-y: hidden !important; }
body { overflow-y: hidden !important; }

.ps-wrap{ background:var(--ps-bg); min-height:100svh; }
@supports (min-height: 100dvh){
  .ps-wrap{ min-height:100dvh; }
}

/* wider container so it uses more of the page */
.ps-container{
  max-width:1280px;
  margin:0 auto;
  padding:56px 40px 40px;
}

/* Utils */
.ps-sr{
  position:absolute !important; width:1px; height:1px; padding:0; margin:-1px;
  overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0;
}
.ps-kbd-focus:focus-visible{
  outline:2px solid color-mix(in oklab, var(--ps-accent), #000 10%);
  outline-offset:2px; border-radius:12px;
}

/* HERO – centered */
.ps-hero{
  max-width:1000px;
  margin:0 auto 56px;
  display:flex;
  justify-content:center;
  text-align:center;
}
.ps-hero > div{
  max-width:1000px;
  width:100%;
}
.ps-eyebrow{
  display:inline-flex; align-items:center; gap:8px; font-weight:600; font-size:13px;
  color:#3f3f46; background:#fff; border:1px solid var(--ps-line);
  border-radius:999px; padding:8px 12px; box-shadow:0 8px 24px rgba(0,0,0,.06)
}
.ps-eyebrow-badge{
  height:18px; min-width:18px; border-radius:999px;
  background:linear-gradient(135deg,var(--ps-accent),var(--ps-accent-2));
  display:inline-flex; align-items:center; justify-content:center;
  font-size:11px; color:#fff; font-weight:700;
}
.ps-title{
  font-size:56px; line-height:1.04; letter-spacing:-.03em;
  font-weight:800; color:var(--ps-black); margin:18px 0 14px;
}
.ps-sub{
  font-size:18px; color:var(--ps-muted); max-width:60ch;
  margin:0 auto;
}

/* HERO CTA – email + button + Google */
.ps-cta{
  display:flex; gap:10px; margin-top:24px;
  align-items:stretch; flex-wrap:wrap; max-width:480px;
  justify-content:center;
  margin-left:auto; margin-right:auto;
}
.ps-cta-note{
  margin-top:8px;
  font-size:12px;
  color:#6b7280;
}

.ps-input{
  flex:1 1 260px; min-width:260px; padding:14px 16px;
  border:1px solid var(--ps-line); border-radius:12px; font-size:16px; background:#fff;
  min-height:44px;
}
.ps-btn{
  padding:14px 18px; border:none; border-radius:12px; font-weight:700; cursor:pointer;
  transition: transform .2s ease, box-shadow .2s ease, filter .2s ease;
  background:linear-gradient(135deg,var(--ps-accent),var(--ps-accent-2));
  color:#fff; min-height:44px; white-space:nowrap;
  box-shadow:0 12px 26px rgba(79,70,229,.35);
}
.ps-btn:hover{
  transform:translateY(-2px);
  box-shadow:0 16px 32px rgba(79,70,229,.4);
  filter:brightness(1.03);
}
.ps-btn.secondary{
  background:#fff; color:#000; border:1px solid var(--ps-line);
  font-weight:600; font-size:14px;
  box-shadow:none;
}

/* Divider between email + Google (Apollo-style) */
.ps-cta-divider{
  margin:14px auto 8px;
  max-width:480px;
  display:flex;
  align-items:center;
  gap:10px;
  font-size:12px;
  color:#9ca3af;
}
.ps-cta-divider::before,
.ps-cta-divider::after{
  content:"";
  flex:1;
  height:1px;
  background:linear-gradient(to right, rgba(148,163,184,.4), rgba(148,163,184,.05));
}

/* Google CTA underneath */
.ps-cta-alt{
  margin-top:12px;
  display:flex;
  justify-content:center;
}
.ps-btn-google{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  padding:13px 18px;
  border-radius:999px;
  border:1px solid #e5e7eb;
  background:#fff;
  color:#111827;
  font-size:14px;
  font-weight:600;
  box-shadow:0 6px 18px rgba(15,23,42,0.08);
  cursor:pointer;
  transition: transform .18s ease, box-shadow .18s ease, background .18s ease;
  min-width:260px;
  max-width:480px;
  width:100%;
}
.ps-btn-google:hover{
  transform:translateY(-1px);
  box-shadow:0 10px 24px rgba(15,23,42,0.12);
  background:#f9fafb;
}
.ps-google-icon{
  width:18px;
  height:18px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
}

.ps-google-icon img{
  display:block;
  width:100%;
  height:100%;
  object-fit:contain;
}

/* Hero trust strip */
.ps-logos{
  display:flex; gap:18px; align-items:center; margin-top:22px;
  opacity:.9; flex-wrap:wrap;
  justify-content:center;
}
.ps-logos span{
  display:inline-flex; align-items:center; gap:8px;
  font-size:14px; color:#4b5563;
}
.ps-dot{ width:4px; height:4px; border-radius:999px; background:#c9ccd6; }

/* Section headers */
.ps-section-head{
  margin-top:56px; margin-bottom:20px;
  max-width:720px;
}
.ps-section-label{
  display:inline-flex; align-items:center; gap:6px;
  font-size:12px; font-weight:700; text-transform:uppercase;
  letter-spacing:.12em; color:#6b7280;
}
.ps-section-dot{
  width:6px; height:6px; border-radius:999px;
  background:linear-gradient(135deg,var(--ps-accent),var(--ps-accent-2));
}
.ps-section-title{
  margin:10px 0 0; font-size:28px; line-height:1.25; color:var(--ps-black);
}

/* HOW IT WORKS – stepped timeline with hierarchy (no boxes) */
.ps-how{
  margin-top:40px;
}
.ps-how-grid{
  display:grid;
  gap:20px;
  position:relative;
  padding-left:30px;
  counter-reset:ps-step;
}
.ps-how-grid::before{
  content:"";
  position:absolute;
  left:8px;
  top:4px;
  bottom:4px;
  width:2px;
  border-radius:999px;
  background:linear-gradient(180deg, rgba(110,91,255,.6), rgba(167,139,250,.15));
}
.ps-how-step{
  display:grid;
  gap:6px;
  padding-left:14px;
  position:relative;
  counter-increment:ps-step;
}
.ps-how-step::before{
  content:counter(ps-step);
  position:absolute;
  left:-30px;
  top:2px;
  width:22px;
  height:22px;
  border-radius:999px;
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:11px;
  font-weight:700;
  color:var(--ps-accent);
  box-shadow:0 8px 20px rgba(15,23,42,0.16);
  border:1px solid rgba(129,140,248,0.6);
}
.ps-step-pill{
  display:inline-flex; align-items:center; gap:6px;
  padding:2px 9px; border-radius:999px;
  font-size:11px; font-weight:700; text-transform:uppercase;
  letter-spacing:.12em;
  color:#4f46e5;
  background:rgba(110,91,255,0.07);
}
.ps-how-step h3{
  margin:4px 0 2px;
  font-size:18px;
  line-height:1.4;
  color:var(--ps-black);
}
.ps-how-step p{
  margin:0;
  font-size:14px;
  line-height:1.6;
  color:var(--ps-muted);
}
.ps-step-meta{
  font-size:11px;
  letter-spacing:.14em;
  text-transform:uppercase;
  color:#9ca3af;
}

/* VALUE STRIP – slimmer radius */
.ps-value-strip{
  margin-top:32px;
  padding:14px 18px;
  border-radius:16px;
  border:1px solid rgba(148,163,184,0.35);
  background:rgba(255,255,255,0.9);
  display:flex;
  flex-wrap:wrap;
  gap:10px 18px;
  align-items:center;
  justify-content:space-between;
}
.ps-value-item{
  display:flex;
  align-items:baseline;
  gap:6px;
  font-size:14px;
  color:#4b5563;
}
.ps-value-item strong{
  font-size:16px;
  color:var(--ps-black);
}

/* FEATURES – bullet list instead of cards */
.ps-feature-section{
  margin:40px auto 0;        /* 🔥 centrování celého bloku */
  max-width:780px;
  text-align:center;         /* headline + text na střed */
}

.ps-feature-heading{
  margin:0 0 8px;
  font-size:24px;
  line-height:1.3;
  color:var(--ps-black);
  text-align:center;         /* explicitně pro jistotu */
}

.ps-feature-text{
  margin:0 0 14px;
  font-size:14px;
  color:var(--ps-muted);
  text-align:center;         /* text pod nadpisem na střed */
}
.ps-feature-list{
  list-style:none;
  margin:0;
  padding:0;
  display:grid;
  gap:8px;
}
@media (min-width: 700px){
  .ps-feature-list{
    grid-template-columns:repeat(2,minmax(0,1fr));
    column-gap:24px;
  }
}
.ps-feature-item{
  display:flex;
  gap:8px;
  align-items:flex-start;
  font-size:14px;
  color:#374151;
}
.ps-feature-bullet{
  width:18px; height:18px; border-radius:999px;
  background:linear-gradient(135deg,var(--ps-accent),var(--ps-accent-2));
  display:inline-flex; align-items:center; justify-content:center;
  color:#fff; font-size:11px; margin-top:2px;
}

/* CTA band */
.ps-cta-band{
  --ring: radial-gradient(40% 140% at 10% 50%, rgba(124,92,255,.25), transparent),
          radial-gradient(40% 140% at 90% 50%, rgba(0,212,255,.25), transparent);
  margin:56px 0 18px; border-radius:22px; padding:18px; display:flex; gap:16px; align-items:center;
  background:
    var(--ring),
    linear-gradient(#fff, #fff) padding-box,
    conic-gradient(from 180deg, rgba(124,92,255,.6), rgba(0,212,255,.6), rgba(124,92,255,.6)) border-box;
  border:1px solid transparent;
  flex-wrap: wrap;
}
.ps-cta-band strong{ font-size:16px; flex:1 1 220px; }
.ps-cta-band .ps-input{ min-width:180px; flex:1 1 200px; }
.ps-cta-band .ps-btn{ flex:0 0 auto; }

/* FAQ – Apollo-style layout (title left, list right) */
.ps-faq{
  margin:60px 0 40px;
  background:#f9fafb;
  border-radius:28px;
  padding:32px 32px 28px;
  border:1px solid #e5e7eb;
}
.ps-faq-layout{
  display:grid;
  grid-template-columns:minmax(0,0.55fr) minmax(0,1.45fr);
  gap:48px;
  align-items:flex-start;
}
.ps-faq-left .ps-section-head{
  margin:0;
}
.ps-faq-left .ps-section-title{
  font-size:32px;
  line-height:1.15;
}
.ps-faq-right{
  display:flex;
  flex-direction:column;
}

/* FAQ links (pricing) */
.ps-faq a{
  color:var(--ps-accent);
  font-weight:500;
  text-decoration:underline;
  text-decoration-thickness:1.5px;
}
.ps-faq a:hover{
  color:var(--ps-accent-2);
}

/* each question is one row */
.ps-faq-item{
  border-radius:0;
  border:none;
  border-bottom:1px solid #e5e7eb;
  background:transparent;
  padding:18px 0;
}
.ps-faq-item:first-of-type{
  padding-top:0;
}
.ps-faq-item:last-of-type{
  border-bottom:none;
}
.ps-faq-item summary{
  list-style:none;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  font-weight:600;
  font-size:15px;
  color:var(--ps-black);
}
.ps-faq-item summary::-webkit-details-marker{ display:none; }
.ps-faq-item summary::after{
  content:"+";
  font-weight:700;
  font-size:22px;
  color:#9ca3af;
}
.ps-faq-item[open] summary::after{
  content:"–";
}
.ps-faq-item p{
  margin:10px 0 4px;
  font-size:14px;
  color:var(--ps-muted);
}

/* Footer */
.ps-footer{
  border-top:1px solid rgba(148,163,184,0.35);
  padding-top:18px;
  margin-top:10px;
}
.ps-footer-inner{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  flex-wrap:wrap;
}
.ps-footer-brand{
  display:flex;
  align-items:center;
  gap:10px;
}
.ps-footer-logo{
  width:26px; height:26px; border-radius:9px;
  display:inline-flex; align-items:center; justify-content:center;
  color:#fff; font-size:16px; font-weight:800;
}
.ps-footer-title{
  font-weight:700; font-size:15px; color:var(--ps-black);
}
.ps-footer-brand p{
  margin:0; font-size:13px; color:#9ca3af;
}
.ps-footer-links{
  display:flex; flex-wrap:wrap; gap:10px;
  font-size:13px;
}
.ps-footer-links a{
  text-decoration:none;
  color:#6b7280;
}
.ps-footer-links a:hover{
  color:var(--ps-black);
  text-decoration:underline;
}
.ps-footer-bottom{
  margin-top:8px;
  font-size:12px;
  color:#9ca3af;
}

/* RESPONSIVE */
@media (max-width: 980px){
  .ps-title{ font-size:44px; }
}

@media (max-width: 720px){
  .ps-faq{
    padding:24px 20px 22px;
    border-radius:22px;
  }
  .ps-faq-layout{
    grid-template-columns:1fr;
    gap:24px;
  }
}

@media (max-width: 700px){
  .ps-value-strip{
    border-radius:14px;
    align-items:flex-start;
  }
  .ps-value-item{
    width:100%;
  }
}

@media (max-width: 560px){
  .ps-container{
    padding-inline:18px;
  }
  .ps-cta{  align-items:stretch }
  .ps-btn, .ps-input{ width:100% }

  .ps-cta-band{
    flex-direction:column; align-items:stretch; gap:10px; padding:14px;
  }
  .ps-cta-band strong{ flex:none }
  .ps-cta-band .ps-input{ width:100%; min-width:0; }
  .ps-cta-band .ps-btn{ width:100%; }

  .ps-logos{ gap:10px }
}

@media (prefers-reduced-motion: reduce){
  .ps-btn, .ps-btn-google{ transition:none }
  .ps-btn:hover, .ps-btn-google:hover{ transform:none; box-shadow:none; filter:none }
}

@keyframes faqFade {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ps-faq-item[open] p {
  animation: faqFade 0.18s ease;
}
`;

const Check = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const GENRES = ["Pop", "Hip-Hop", "EDM", "DNB", "House", "Indie", "Rock", "R&B", "Lo-fi", "Chill"];
const GOOGLE_LOGO_SRC = "https://i.imgur.com/haJs99r.png";

export default function Home() {
  const seo = seoPresets["/"];
  const [email, setEmail] = useState("");
  const { googleLogin } = useAuth();

  const goToSignup = (opts = {}) => {
    const params = new URLSearchParams();
    if (email && !opts.method) {
      params.set("email", email);
    }
    if (opts.method) {
      params.set("method", opts.method);
    }
    const qs = params.toString();
    window.location.href = "/signup" + (qs ? `?${qs}` : "");
  };

  async function onHeroGoogleSuccess(cred) {
    try {
      await googleLogin(cred.credential);
      window.location.href = "/"; // nebo /app, /playlists, podle tvýho routeru
    } catch (e) {
      console.error(e);
    }
  }


  return (
    <>
    <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
      />
      <div className="ps-wrap">
        <style>{css}</style>

        <div className="page-scroll">
          <div className="ps-container">
            {/* HERO – centered */}
            <section className="ps-hero">
              <div>
                <span className="ps-eyebrow">
                  <span className="ps-eyebrow-badge">MB</span>
                  <span>Find the right playlists. Reach the right people. Grow faster.</span>
                </span>
                <h1 className="ps-title">
                  Find the right playlists. Reach the right people.
                  <br />
                  Grow faster.
                </h1>
                <p className="ps-sub">
                  Find the right playlists, contact the curators, and give your music a real shot at placement.
                </p>

                {/* Email CTA */}
                <form
                  className="ps-cta"
                  onSubmit={(e) => {
                    e.preventDefault();
                    goToSignup();
                  }}
                  aria-label="Sign up for MusicBase"
                >
                  <label htmlFor="hero-email" className="ps-sr">
                    Email
                  </label>
                  <input
                    id="hero-email"
                    className="ps-input ps-kbd-focus"
                    type="email"
                    placeholder="you@artistname.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <button className="ps-btn ps-kbd-focus" type="submit">
                    Sign up free
                  </button>
                </form>

                {/* Divider like Apollo */}
                <div className="ps-cta-divider" aria-hidden="true">
                  <span>or</span>
                </div>

                {/* Google CTA under it */}
                <div className="ps-cta-alt">
                  <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
                    <GoogleLogin
                      onSuccess={onHeroGoogleSuccess}
                      onError={() => console.error("Google sign-in failed")}
                      shape="pill"
                      text="continue_with"
                      width="260"
                    />
                  </GoogleOAuthProvider>
                </div>

                <p className="ps-cta-note">
                  Built for artists. Credit card required. Cancel anytime.
                </p>

                <div className="ps-logos" aria-label="MusicBase highlights">
                  <span>🎧 12,000+ playlists with contact</span>
                  <span className="ps-dot" aria-hidden="true" />
                  <span>
                    <Check /> Filter by email, Instagram, or both
                  </span>
                  <span className="ps-dot" aria-hidden="true" />
                  <span>
                    <Check /> Campaigns that send emails &amp; DMs from your own accounts
                  </span>
                </div>
              </div>
            </section>

            {/* HOW IT WORKS – improved timeline */}
            <section className="ps-how" aria-label="How MusicBase works">
              <header className="ps-section-head">
                <p className="ps-section-label">
                  <span className="ps-section-dot" />
                  <span>How it works</span>
                </p>
                <h2 className="ps-section-title">
                  Turn “How do I promote my music?” into a simple, repeatable marketing process.
                </h2>
              </header>

              <div className="ps-how-grid">
                <div className="ps-how-step">
                  <span className="ps-step-pill">Step 1 · Search</span>
                  <h3>Search by genres that match your sound</h3>
                  <p>
                    Choose genres like {GENRES.slice(0, 4).join(", ")} and more. MusicBase scans our playlist database
                    around your sound — not random catch-all lists.
                  </p>
                  <div className="ps-step-meta">Perfect for singles, EPs, albums, and catalog pushes.</div>
                </div>

                <div className="ps-how-step">
                  <span className="ps-step-pill">Step 2 · Filter</span>
                  <h3>Select email, IG, or both</h3>
                  <p>
                    Show only playlists with <strong>email</strong>, only <strong>Instagram</strong>, or{" "}
                    <strong>both</strong>. Add filters for follower range and recent activity so you skip dead or fake
                    lists.
                  </p>
                  <div className="ps-step-meta">No more guessing who&apos;s behind the playlist.</div>
                </div>

                <div className="ps-how-step">
                  <span className="ps-step-pill">Step 3 · Templates</span>
                  <h3>Write your own pitch once</h3>
                  <p>
                    Save your email and DM pitches as templates — introduce yourself, drop in your links, and keep the
                    parts that stay the same from release to release.
                  </p>
                  <div className="ps-step-meta">Sounds like you, not a generic copy-paste blast.</div>
                </div>

                <div className="ps-how-step">
                  <span className="ps-step-pill">Step 4 · Campaign</span>
                  <h3>Add curators and choose when sends go out</h3>
                  <p>
                    Add the curators you like into a campaign, then choose whether to send messages immediately or on a
                    delay (for example, in 30 minutes) so everything rolls out smoothly.
                  </p>
                  <div className="ps-step-meta">Helps solo artists feel organized like a small team.</div>
                </div>
              </div>
            </section>

            {/* VALUE STRIP */}
            <section className="ps-value-strip" aria-label="MusicBase at a glance">
              <div className="ps-value-item">
                <strong>12,000+</strong>
                <span>playlists with contact details, curated around real genres artists actually release in.</span>
              </div>
              <div className="ps-value-item">
                <strong>Email &amp; IG</strong>
                <span>
                  filter campaigns by email only, Instagram only, or both — the same way you already reach out to curators.
                </span>
              </div>
              <div className="ps-value-item">
                <strong>Minutes</strong>
                <span>to build your first campaign, not weeks of copy-pasting and chasing down curator profiles.</span>
              </div>
            </section>

            {/* FEATURES */}
            <section className="ps-feature-section" aria-label="Why artists use MusicBase">
              <h2 className="ps-feature-heading">Why artists use MusicBase for playlist outreach</h2>
              <p className="ps-feature-text">
                Instead of juggling spreadsheets, random DMs, and old notes, you keep your entire playlist outreach in one
                place — so every release has a clear, repeatable plan.
              </p>
              <ul className="ps-feature-list">
                <li className="ps-feature-item">
                  <span className="ps-feature-bullet">
                    <Check />
                  </span>
                  <span>Curator-ready playlist database with contact info and basic quality signals.</span>
                </li>
                <li className="ps-feature-item">
                  <span className="ps-feature-bullet">
                    <Check />
                  </span>
                  <span>Search by genre, mood, territory, and contact channel in a few clicks.</span>
                </li>
                <li className="ps-feature-item">
                  <span className="ps-feature-bullet">
                    <Check />
                  </span>
                  <span>Save your own email and DM templates so every release takes less time to pitch.</span>
                </li>
                <li className="ps-feature-item">
                  <span className="ps-feature-bullet">
                    <Check />
                  </span>
                  <span>Track replies so you know who opened, who replied, and who to follow up with next.</span>
                </li>
              </ul>
            </section>

            {/* Signup CTA (secondary, lower on page) */}
            <section className="ps-cta-band" aria-label="Get started with MusicBase">
              <strong>Ready to line up playlists for your next release in one search?</strong>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flex: "1 1 auto",
                  flexWrap: "wrap",
                }}
              >
                <label htmlFor="signup-email" className="ps-sr">
                  Email
                </label>
                <input
                  id="signup-email"
                  className="ps-input ps-kbd-focus"
                  placeholder="you@artistname.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      goToSignup();
                    }
                  }}
                />
                <button className="ps-btn ps-kbd-focus" type="button" onClick={() => goToSignup()}>
                  Start free
                </button>
              </div>
            </section>

            {/* FAQ */}
            <section className="ps-faq" aria-label="Frequently asked questions">
              <div className="ps-faq-layout">
                <div className="ps-faq-left">
                  <header className="ps-section-head" style={{ marginTop: 0 }}>
                    <p className="ps-section-label">
                      <span className="ps-section-dot" />
                      <span>FAQ</span>
                    </p>
                    <h2 className="ps-section-title">Frequently asked questions</h2>
                  </header>
                </div>

                <div className="ps-faq-right">
                  <details className="ps-faq-item">
                    <summary>Why search for independent playlists?</summary>
                    <p>
                      Independent playlists are more accessible now than ever before. Real listeners are out there using playlists to discover new music right now. It’s one of the most effective ways for independent artists to reach their potential fanbase.
                    </p>
                  </details>

                  <details className="ps-faq-item">
                    <summary>How does the software work?</summary>
                    <p>
                      We analyze public spotify playlists to collect all information you would need to reach the owners.
                    </p>
                  </details>

                  <details className="ps-faq-item">
                    <summary>Does MusicBase.io provide a large database of playlist contacts?</summary>
                    <p>
                      Yes. We are constantly updating our database to provide the most up-to-date information for all playlists on spotify. We currently have over 12K+ playlists in our database.
                    </p>
                  </details>

                  <details className="ps-faq-item">
                    <summary>Does MusicBase.io automate outreach sequences and follow-up?</summary>
                    <p>
                      MusicBase.io automates outreach sequences and follow-ups by offering built-in email sequencing, which saves time and helps artists maintain consistent engagement with playlist owners. No more writing emails to every playlist curator manually.
                    </p>
                  </details>

                  <details className="ps-faq-item">
                    <summary>How is MusicBase better than other software platforms?</summary>
                    <p>
                      Our platform includes email features you can't get anywhere else. It's also faster and cheaper than any other alternative on the market.
                    </p>
                  </details>

                  <details className="ps-faq-item">
                    <summary>How much does a MusicBase.io subscription cost?</summary>
                    <p>
                      Please see our <a href="/pricing">pricing</a> page for more information.
                    </p>
                  </details>
                </div>
              </div>
            </section>

            {/* FOOTER */}
            <footer className="ps-footer">
              <div className="ps-footer-inner">
                <div className="ps-footer-brand">
                  <span className="ps-footer-logo" aria-hidden>
                    <img
                      className="brand-icon"
                      src="https://www.musicbase.io/logo.png"
                      alt=""
                      aria-hidden
                      style={{ height: "25px", width: "25px" }}
                    />
                  </span>
                  <div>
                    <div className="ps-footer-title">MusicBase</div>
                    <p>Playlist outreach built for artists, not sales teams.</p>
                  </div>
                </div>

                <nav className="ps-footer-links" aria-label="Footer">
                  <a href="/">Home</a>
                  <a href="/pricing">Pricing</a>
                  <a href="/login">Log in</a>
                  <a href="/signup">Sign up</a>
                  <a href="/contact">Contact</a>
                  <a href="/terms">Terms</a>
                  <a href="/privacy">Privacy Policy</a>
                </nav>
              </div>
              <div className="ps-footer-bottom">
                © {new Date().getFullYear()} MusicBase. All rights reserved.
              </div>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}
